import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { visuallyHidden } from '@mui/utils';
import { ApiInstance } from '../../../api/api';
import { SwitchPortsDataContext } from '../../../context/switchPortsDataContext';
import {
  DeviceStackDataType,
  DeviceStackErrorDataType,
  DeviceSwitchPortsDataType,
} from '../../../types/buildingTypes';
import { DataSourceType, Order } from '../../../types/types';
import SwitchPortsSearch from '../../searches/SwitchPortsSearch';
import { useSearchParams } from 'react-router-dom';
import TablePlaceholder from '../../placeholders/TablePlaceholder';
import DeviceProcessingError from '../../placeholders/DeviceProcessingError';
import SwitchPortsFilterMenu, {
  DEFAULT_SWITCH_PORTS_FILTER_STATE,
} from '../../filters/SwitchPortsFilterMenu';
import { formatMacAddress } from '../../../utils/macFormatter';
import MacDataDialog from '../../dialogs/MacDataDialog';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import StackTable from './StackTable';
import DataSourceSwitcher from '../../switchers/DataSourceSwitcher';
import DeviceButtonTooltip from '../../placeholders/DeviceButtonTooltip';
import TableNoDataPlaceholder from '../../placeholders/TableNoDataPlaceholder';
import TableNoResults from '../../placeholders/TableNoResults';
import s from '../../placeholders/DeviceProcessingError.module.css';
import Divider from '@mui/material/Divider';
import { red } from '@mui/material/colors';
import { getComparator, stableSort } from '../../../utils/tableHelpers';

interface HeadSwitchPortsCell {
  disablePadding: boolean;
  id: keyof DeviceSwitchPortsDataType;
  label: string;
  numeric: boolean;
}

const headSwitchPortsCells: readonly HeadSwitchPortsCell[] = [
  {
    id: 'port',
    numeric: false,
    disablePadding: false,
    label: 'Port',
  },
  {
    id: 'profile',
    numeric: false,
    disablePadding: false,
    label: 'Profile',
  },
  {
    id: 'switch_serial',
    numeric: false,
    disablePadding: false,
    label: 'Serial',
  },
  {
    id: 'vlan',
    numeric: false,
    disablePadding: false,
    label: 'VLAN',
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: 'Status',
  },
  {
    id: 'poe',
    numeric: false,
    disablePadding: false,
    label: 'POE',
  },
  {
    id: 'speed',
    numeric: false,
    disablePadding: false,
    label: 'Speed',
  },
  {
    id: 'uptime',
    numeric: false,
    disablePadding: false,
    label: 'Uptime',
  },
  {
    id: 'description',
    numeric: false,
    disablePadding: false,
    label: 'Description',
  },
  {
    id: 'mac_address',
    numeric: false,
    disablePadding: false,
    label: 'Mac address',
  },
];

interface SwitchPortsTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof DeviceSwitchPortsDataType
  ) => void;
  order: Order;
  orderBy: string;
  isApiMode: boolean;
  isJuniper: boolean;
  isMeraki: boolean;
}

function SwitchPortsTableHead(props: SwitchPortsTableProps) {
  const { order, orderBy, onRequestSort, isApiMode, isJuniper, isMeraki } = props;

  const filteredSwitchPortsCells = headSwitchPortsCells.filter((item: HeadSwitchPortsCell) => {
    if (item.id === 'profile') return isApiMode && isJuniper;
    if (item.id === 'switch_serial') return isMeraki;
    return true;
  });

  const createSortHandler =
    (property: keyof DeviceSwitchPortsDataType) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {filteredSwitchPortsCells.map((HeadSwitchPortsCell) => (
          <TableCell
            key={HeadSwitchPortsCell.id}
            align={HeadSwitchPortsCell.numeric ? 'right' : 'left'}
            padding={HeadSwitchPortsCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === HeadSwitchPortsCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === HeadSwitchPortsCell.id}
              direction={orderBy === HeadSwitchPortsCell.id ? order : 'asc'}
              onClick={createSortHandler(HeadSwitchPortsCell.id)}
            >
              {HeadSwitchPortsCell.label}
              {orderBy === HeadSwitchPortsCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface TableToolbarProps {
  isJuniper: boolean;
  isApiMode: boolean;
  title: string;
  entity: string;
  search: boolean;
  navigationFilter?: boolean;
  navigationFilterName?: string;
  navigationFilterValue?: string;
  hasUnreportedConfiguration: boolean;
}

function TableToolbar(props: TableToolbarProps) {
  const {
    isJuniper,
    isApiMode,
    title,
    entity,
    search,
    navigationFilter,
    navigationFilterName,
    navigationFilterValue,
    hasUnreportedConfiguration,
  } = props;
  const [filterInput, setFilterInput] = React.useState(DEFAULT_SWITCH_PORTS_FILTER_STATE);

  return (
    <>
      <Toolbar>
        <Typography sx={{ flex: '1 1 100%' }} variant="h5" id="tableTitle" component="div">
          {title}: {entity}
        </Typography>
        {search && <SwitchPortsSearch isApiMode={isApiMode} />}
        <SwitchPortsFilterMenu
          filterState={filterInput}
          setFilterState={setFilterInput}
          navigationFilter={navigationFilter}
          navigationFilterName={navigationFilterName}
          navigationFilterValue={navigationFilterValue}
          isApiMode={isApiMode}
        />
      </Toolbar>
      {hasUnreportedConfiguration && isApiMode && isJuniper ? (
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
          }}
        >
          <Typography color={red[700]}>
            * The interface configuration has been updated in the Juniper dashboard, but has not yet
            been applied to the device.
          </Typography>
        </Toolbar>
      ) : null}
    </>
  );
}

interface SwitchPortsTypeTableProps {
  device: string;
  deviceIP: string;
  selectedRegion: string;
  selectedBuilding: string;
  selectedVendor: string;
  selectedSite: string;
}

interface FilterMap {
  [key: string]: string;
}

export default function SwitchPortsTable(props: SwitchPortsTypeTableProps) {
  const { device, deviceIP, selectedRegion, selectedBuilding, selectedVendor, selectedSite } =
    props;
  const isJuniper: boolean = selectedVendor === 'juniper';
  const isMeraki: boolean = selectedVendor === 'meraki';
  const isSwitchDevice: boolean = device === 'switch';
  const [isLoading, setIsLoading] = React.useState(false);
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof DeviceSwitchPortsDataType>('port');
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [allSwitchPorts, setAllSwitchPorts] = React.useState<DeviceSwitchPortsDataType[]>([]);
  const [rowsPerPage, setRowsPerPage] = React.useState(30);
  const [switchPorts, setSwitchPorts] = React.useState<DeviceSwitchPortsDataType[]>([]);
  const [stackData, setStackData] = React.useState<
    DeviceStackDataType[] | DeviceStackErrorDataType
  >([]);
  const [apiProcessingErrorFlag, setApiProcessingErrorFlag] = React.useState<Boolean>(false);
  const [stackApiProcessingErrorFlag, setStackApiProcessingErrorFlag] =
    React.useState<Boolean>(false);
  const [stackLegacyDeviceError, setStackLegacyDeviceError] = React.useState<Boolean>(false);
  const [loadingState, setLoadingState] = React.useState<Boolean>(false);
  const [open, setOpen] = React.useState(false);
  const [macData, setMacData] = React.useState([]);
  const [selectedPort, setSelectedPort] = React.useState<string>('');
  const [searchParams] = useSearchParams();
  const [navigationFilter, setNavigation] = React.useState(false);
  const [navigationFilterName, setNavigationFilterName] = React.useState<string>('');
  const [navigationFilterValue, setNavigationFilterValue] = React.useState<string>('');
  const [dataSourceType, setDataSourceType] = React.useState<DataSourceType>(
    (isJuniper || isMeraki) && isSwitchDevice ? 'api' : 'cli'
  );
  const isApiMode: boolean = dataSourceType === 'api';
  const filterMap: FilterMap = {
    'filter[port]': 'port',
    'filter[mac_address]': 'mac_address',
  };
  const hasUnreportedConfiguration: boolean = allSwitchPorts.some(
    (port) => port.unreported_configuration
  );

  const clearStates = () => {
    setAllSwitchPorts([]);
    setSwitchPorts([]);
    setStackData([]);
  };

  const extractNavigationFilterParams = (searchParams: URLSearchParams, filterMap: FilterMap) => {
    for (const [key, value] of Array.from(searchParams.entries())) {
      if (key.startsWith('filter') && filterMap[key]) {
        return {
          navigationFilter: true,
          navigationFilterName: filterMap[key],
          navigationFilterValue: value,
        };
      }
    }

    return {
      navigationFilter: false,
      navigationFilterName: '',
      navigationFilterValue: '',
    };
  };

  const getSwitchData = async () => {
    setIsLoading(true);
    const { navigationFilter, navigationFilterName, navigationFilterValue } =
      extractNavigationFilterParams(searchParams, filterMap);
    const legacyDeviceResponse: string = 'Invalid input detected at \'^\' marker.';

    if (device === 'distro' || device === 'isp') {
      try {
        const distro_switch_ports = await ApiInstance.getSwitch(
          deviceIP,
          selectedVendor,
          selectedBuilding,
          selectedRegion,
          device,
          selectedSite,
          isMeraki
        );
        setAllSwitchPorts(distro_switch_ports.switch_ports_data);
        setSwitchPorts(distro_switch_ports.switch_ports_data);
        setNavigation(navigationFilter);
        setNavigationFilterName(navigationFilterName);
        setNavigationFilterValue(navigationFilterValue);
        setStackData(
          distro_switch_ports.stack_data?.error === true ? [] : distro_switch_ports.stack_data
        );
        setStackApiProcessingErrorFlag(distro_switch_ports.stack_data?.error === true);
        setStackLegacyDeviceError(distro_switch_ports.stack_data?.detail === legacyDeviceResponse);
      } catch (e: any) {
        setStackApiProcessingErrorFlag(true);
        setApiProcessingErrorFlag(true);
      }
    } else {
      try {
        const api_mode: boolean = dataSourceType === 'api';

        clearStates();

        const switch_stack = await ApiInstance.getSwitch(
          deviceIP,
          selectedVendor,
          selectedBuilding,
          selectedRegion,
          device,
          selectedSite,
          api_mode
        );

        setAllSwitchPorts(switch_stack.switch_ports_data);
        setSwitchPorts(switch_stack.switch_ports_data);
        setStackData(switch_stack.stack_data);
        setNavigation(navigationFilter);
        setNavigationFilterName(navigationFilterName);
        setNavigationFilterValue(navigationFilterValue);
        setStackData(switch_stack.stack_data?.error === true ? [] : switch_stack.stack_data);
        setStackApiProcessingErrorFlag(switch_stack.stack_data?.error === true);
        setStackLegacyDeviceError(switch_stack.stack_data?.detail === legacyDeviceResponse);
      } catch (e: any) {
        setStackApiProcessingErrorFlag(true);
        setApiProcessingErrorFlag(true);
      }
    }

    setIsLoading(false);
  };

  React.useEffect(() => {
    getSwitchData();
  }, [dataSourceType]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof DeviceSwitchPortsDataType
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const openMacDataDialog = (data: any, portName: string) => {
    setMacData(data);
    setSelectedPort(portName);
    setOpen(true);
  };

  const closeMacDataDialog = () => {
    setOpen(false);
    setMacData([]);
    setSelectedPort('');
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - switchPorts.length) : 0;

  const switchPortsRows = React.useMemo(
    () =>
      stableSort(switchPorts, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, switchPorts]
  );

  const column_labels = headSwitchPortsCells
    .filter((column) => {
      if (column.id === 'profile') return isApiMode && isJuniper;
      if (column.id === 'switch_serial') return isMeraki;
      return true;
    })
    .map((item) => item.label);

  const renderMacAddresses = (mac_addresses: string) =>
    Object.values(mac_addresses).map((mac_address) => (
      <Typography variant="body2">{formatMacAddress(mac_address)}</Typography>
    ));

  const getRowTooltipContent = (
    port: string,
    configuredProfile: string,
    configuredDescription: string,
    dynamicPortConfiguration: string
  ): React.ReactNode => (
    <React.Fragment>
      <Typography variant="body2" className={s.row_header_tooltip}>
        Configuration state for <b>{port}</b>
      </Typography>
      <Divider className={s.host_divider_tooltip} />
      <Typography variant="body2" className={s.row_body_tooltip}>
        Configured profile: {configuredProfile || 'N/A'} <br />
        Configured description: {configuredDescription || 'N/A'}
        <br />
        Dynamic port configuration: {dynamicPortConfiguration || 'N/A'}
      </Typography>
    </React.Fragment>
  );

  return (
    <SwitchPortsDataContext.Provider
      value={{
        allSwitchPorts,
        switchPorts,
        loadingState,
        setSwitchPorts,
        setLoadingState,
      }}
    >
      {isJuniper && isSwitchDevice ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <DataSourceSwitcher selected={dataSourceType} onChange={setDataSourceType} />
        </Box>
      ) : null}
      <StackTable
        device={device}
        selectedSite={selectedSite}
        arpEntries={stackData}
        isLoading={isLoading}
        apiErrorFlag={stackApiProcessingErrorFlag}
        legacyDeviceError={stackLegacyDeviceError}
        isMeraki={isMeraki}
      />
      <Box sx={{ width: '100%', margin: '5pt' }} id={'switch-ports'}>
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableToolbar
            isJuniper={isJuniper}
            isApiMode={isApiMode}
            title={`Switch ports for selected ${device}`}
            entity={`${selectedSite}`}
            search={true}
            navigationFilter={navigationFilter}
            navigationFilterName={navigationFilterName}
            navigationFilterValue={navigationFilterValue}
            hasUnreportedConfiguration={hasUnreportedConfiguration}
          />
          {isLoading ? (
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <TablePlaceholder rows={10} columns={column_labels} />
            </Box>
          ) : (
            <TableContainer sx={{ minHeight: '30rem' }}>
              <Table
                sx={{ minWidth: 750 }}
                aria-labelledby="tableTitle"
                size={dense ? 'small' : 'medium'}
              >
                <SwitchPortsTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  isApiMode={isApiMode}
                  isJuniper={isJuniper}
                  isMeraki={isMeraki}
                />
                <TableBody>
                  {switchPortsRows.map((row, index) => {
                    const labelId = `switch-ports-table-checkbox-${index}`;

                    return (
                      <DeviceButtonTooltip
                        title={getRowTooltipContent(
                          row.port,
                          row.configured_profile,
                          row.configured_description,
                          row.dynamic_port_configuration
                        )}
                        PopperProps={{
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, -9],
                              },
                            },
                          ],
                        }}
                        disableHoverListener={!isApiMode}
                      >
                        <TableRow hover sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          {isApiMode && isJuniper && isSwitchDevice ? (
                            <TableCell component="th" id={labelId} scope="row" padding="normal">
                              {row.port}
                              {row.port_parent?.trim() && ` (${row.port_parent})`}
                              {row.port_children?.trim() && ` [${row.port_children}]`}
                              {row.unreported_configuration && (
                                <Typography
                                  component="span"
                                  display="inline"
                                  color="error"
                                  fontWeight="bold"
                                  sx={{ padding: 0, margin: 0 }}
                                  style={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 200,
                                  }}
                                >
                                  *
                                </Typography>
                              )}
                            </TableCell>
                          ) : (
                            <TableCell component="th" id={labelId} scope="row" padding="normal">
                              {row.port}
                            </TableCell>
                          )}
                          {isApiMode && isJuniper && isSwitchDevice ? (
                            <TableCell
                              style={{
                                whiteSpace: 'nowrap',
                                overflowX: 'auto',
                              }}
                            >
                              {row.profile}
                              {row.dynamic && (
                                <Box
                                  component="span"
                                  sx={{
                                    backgroundColor: '#bbdefb',
                                    color: '#0d47a1',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    padding: '1px 4px',
                                    borderRadius: '6px',
                                    whiteSpace: 'nowrap',
                                    marginLeft: '2px',
                                  }}
                                >
                                  Dynamic
                                </Box>
                              )}
                            </TableCell>
                          ) : null}
                          {isMeraki ? (
                            <TableCell
                              style={{
                                whiteSpace: 'nowrap',
                                overflowX: 'auto',
                              }}
                            >
                              {row.switch_serial} ({row.switch_name})
                            </TableCell>
                          ) : null}
                          <TableCell>{row.vlan}</TableCell>
                          <TableCell>{row.status}</TableCell>
                          <TableCell>{row.poe}</TableCell>
                          <TableCell>{row.speed}</TableCell>
                          <TableCell>{row.uptime}</TableCell>
                          <TableCell>{row.description}</TableCell>
                          {(() => {
                            if (row.mac_address.length > 1) {
                              return (
                                <TableCell>
                                  <Button
                                    variant="outlined"
                                    endIcon={<DynamicFeedIcon fontSize={'large'} />}
                                    onClick={() => openMacDataDialog(row.mac_address, row.port)}
                                  >
                                    multiple
                                  </Button>
                                </TableCell>
                              );
                            } else {
                              return <TableCell>{renderMacAddresses(row.mac_address)}</TableCell>;
                            }
                          })()}
                        </TableRow>
                      </DeviceButtonTooltip>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow
                      style={{
                        height: (dense ? 33 : 53) * emptyRows,
                      }}
                    >
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                  {allSwitchPorts.length === 0 && !apiProcessingErrorFlag && (
                    <TableRow>
                      <TableCell colSpan={headSwitchPortsCells.length}>
                        <TableNoDataPlaceholder entity="Access switch" />
                      </TableCell>
                    </TableRow>
                  )}
                  {switchPorts.length === 0 && allSwitchPorts.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={headSwitchPortsCells.length}>
                        <TableNoResults />
                      </TableCell>
                    </TableRow>
                  )}
                  {apiProcessingErrorFlag && (
                    <TableRow>
                      <TableCell colSpan={headSwitchPortsCells.length}>
                        <DeviceProcessingError />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            rowsPerPageOptions={[10, 20, 30, { value: switchPorts.length, label: 'All' }]}
            component="div"
            count={switchPorts.length}
            rowsPerPage={rowsPerPage}
            labelRowsPerPage="Rows per table:"
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        <FormControlLabel
          control={<Switch checked={dense} onChange={handleChangeDense} />}
          label="Dense padding"
        />
      </Box>
      <MacDataDialog
        open={open}
        handleClose={closeMacDataDialog}
        portName={selectedPort}
        macData={macData}
      />
    </SwitchPortsDataContext.Provider>
  );
}
